<template>
  <div class="form-control min-w-40 w-full h-14 @[480px]:h-16">
    <div class="join w-full h-full">

      <!-- INPUT TAGS -->
      <div v-show="selectedOptions.length > 0"
        class="flex items-center flex-wrap gap-2 rounded-lg px-3 py-2 bg-base-100 shadow-sm focus-within:ring-2 focus-within:ring-primary transition-all h-full rounded-l-xl">
        <!-- Tags -->
        <div v-if="selectedOptions.length > 0" class="flex items-center gap-2">
          <!-- Un tag -->
          <span v-if="selectedOptions.length === 1"
            class="badge badge-primary max-w-[130px] truncate whitespace-nowrap flex items-center px-3 py-1 text-white text-sm">
            {{ selectedOptions[0].nombre.length > 15 ? selectedOptions[0].nombre.substring(0, 15) + '...' :
              selectedOptions[0].nombre }}
            <button @click="removeTag(selectedOptions[0])"
              class="ml-2 text-white font-bold hover:text-red-200">&times;</button>
          </span>

          <!-- Múltiples tags -->
          <span v-else class="badge badge-primary flex items-center px-3 py-1 text-white text-sm truncate">
            {{ selectedOptions.length }} ubicaciones
            <button @click="selectedOptions = []" class="ml-2 text-white font-bold hover:text-red-200">&times;</button>
          </span>
        </div>
      </div>


      <!-- SEARCH BUTTON -->
      <div class="flex items-center justify-center border border-base-300 bg-base-100 p-[7px] rounded-r-xl w-full"
        :class="{ 'rounded-l-xl': selectedOptions.length == 0 }">
        <!-- <MultiTagSelect :options="options" :searchMethod="searchLocations" keyId="id" keyLabel="nombre"/> -->
        <div class="relative w-full">
          <!-- Search container with rounded corners -->
          <div class="flex items-center w-full shadow-sm overflow-hidden">
            <!-- Search icon -->
            <div class="pl-4 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <!-- Input field -->
            <input type="text" v-model="searchQuery" @input="() => filterOptions()" @focus="showDropdown = true"
              @blur="onBlur" class="w-full px-3 py-3 focus:outline-none max-w-[480px] bg-base-100"
              :placeholder="placeholder" />

            <!-- Search button -->
            <!-- <button 
              @click="$emit('search', searchQuery)"
              class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 font-medium"
            >
              {{ buttonText }}
            </button> -->

            <!-- Property Type Selector -->
            <div class="flex items-center gap-2 px-3 w-1/2">
              <select v-model="propertyType" class="select select-bordered w-full focus:outline-none">
                <option value="casa" selected>Casa</option>
                <option value="departamento">Departamento</option>
                <option value="local">Local</option>
                <option value="ph">PH</option>
                <option value="cochera">Cochera</option>
              </select>
            </div>

            <!-- Transaction Type Selector -->
            <div class="flex items-center gap-2 px-3 w-1/2">
              <select v-model="transactionType" class="select select-bordered w-full focus:outline-none">
                <option value="venta" selected>Venta</option>
                <option value="alquiler">Alquiler</option>
              </select>
            </div>

            <button
              class="btn btn-primary min-w-[84px] h-10 px-4 @[480px]:h-12 @[480px]:px-5 text-sm font-bold leading-normal tracking-[0.015em] @[480px]:text-base"
              :disabled="selectedOptions.length === 0" @click="redirectToSearch">
              <span class="truncate">Buscar</span>
            </button>
          </div>

          <!-- Dropdown with options -->
          <div v-if="showDropdown && filteredOptions.length > 0"
            class="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
            <div v-for="(option, index) in filteredOptions" :key="option.id" @mousedown.prevent="selectOption(option)"
              class="px-4 py-2 cursor-pointer hover:bg-gray-100" :class="{ 'bg-gray-100': highlightedIndex === index }">
              {{ option.nombre }}
            </div>
          </div>

          <!-- No results message -->
          <div v-if="showDropdown && searchQuery && filteredOptions.length === 0"
            class="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg p-4 text-gray-500">
            No se encontraron resultados
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import { mapActions, mapMutations } from "vuex";
import { parseFiltersFromUrl, buildFilters } from '@/utils/filters';

export default {
  name: 'SearchBar',
  components: {
    Select: () => import('../../components/common/Select.vue'),
  },
  data() {
    return {
      options: [],
      searchQuery: '',
      filteredOptions: [],
      showDropdown: false,
      highlightedIndex: -1,
      placeholder: 'Buenos Aires, CABA, La Plata',
      buttonText: 'Buscar',
      selectedOptions: [],
      propertyType: "casa",
      transactionType: "venta",
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          const selectedOption = this.options.find(option => option.value === newValue);
          if (selectedOption) {
            this.searchQuery = selectedOption.label;
          }
        }
      }
    },
    options: {
      immediate: true,
      handler(newOptions) {
        this.filterOptions();
      }
    },
    '$route.query': {
      handler(newFilters, oldFilters) {
        const filtros = parseFiltersFromUrl(newFilters);
        this.propertyType = filtros.property_type || 'casa';
        this.transactionType = filtros.transaction_type || 'venta';
        this.selectedOptions = filtros.locations || [];
      },
      immediate: true
    },
    propertyType: {
      handler(newValue, oldValue) {
        if (oldValue && newValue !== oldValue) {
          this.redirectToSearch();
        }
      }
    },
    transactionType: {
      handler(newValue, oldValue) {
        if (oldValue && newValue !== oldValue) {
          this.redirectToSearch();
        }
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyDown);
    const filtros = parseFiltersFromUrl(this.$route.query);
    this.propertyType = filtros.property_type || 'casa';
    this.transactionType = filtros.transaction_type || 'venta';
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    ...mapActions({
      searchLocations: 'SEARCH_LOCATIONS',
    }),
    ...mapMutations({
      updateFilters: 'UPDATE_FILTERS',
    }),
    // buscarLocalidad: _.debounce(async function (nombre) {
    //   // const response = await fetch(`https://apis.datos.gob.ar/georef/api/localidades?nombre=${nombre}&aplanar=true&max=10`);
    //   this.options = await this.searchLocations(nombre);
    // }, 500),
    async filterOptions() {
      if (!this.searchQuery) {
        this.filteredOptions = [...this.options];
        return;
      }

      this.filteredOptions = await this.searchLocations(this.searchQuery);
      this.highlightedIndex = this.filteredOptions.length > 0 ? 0 : -1;
    },
    selectOption(option) {
      // this.searchQuery = option.nombre;
      // this.showDropdown = false;
      this.$emit('input', option.nombre);
      this.$emit('select', option);
      this.selectedOptions.push(option);
      this.searchQuery = '';
      this.options = [];
    },
    onBlur() {
      // Delay hiding dropdown to allow click events to complete
      setTimeout(() => {
        this.showDropdown = false;
      }, 200);
    },
    handleKeyDown(e) {
      if (!this.showDropdown) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          this.highlightedIndex = Math.min(
            this.highlightedIndex + 1,
            this.filteredOptions.length - 1
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
          break;
        case 'Enter':
          if (this.highlightedIndex >= 0) {
            e.preventDefault();
            this.selectOption(this.filteredOptions[this.highlightedIndex]);
          }
          break;
        case 'Escape':
          this.showDropdown = false;
          break;
      }
    },
    redirectToSearch() {
      const { queryString } = buildFilters({
        locations: this.selectedOptions,
        property_type: this.propertyType,
        transaction_type: this.transactionType,
      });

      const targetRoute = `/search?${queryString}`;
      
      console.log('SearchBar redirecting to:', targetRoute);
      console.log('Selected options:', this.selectedOptions);
      console.log('Property type:', this.propertyType);
      console.log('Transaction type:', this.transactionType);
      
      if (this.$route.fullPath !== targetRoute) {
        this.$router.push(targetRoute);
      } else {
        // If we're already on the same route, emit a search event
        this.$emit('search', {
          locations: this.selectedOptions,
          property_type: this.propertyType,
          transaction_type: this.transactionType,
        });
      }
    },
    
    removeTag(option) {
      const index = this.selectedOptions.findIndex(selected => selected.id === option.id);
      if (index > -1) {
        this.selectedOptions.splice(index, 1);
      }
    }
  },
  computed: {
    // ...mapState({
    //   transaction_type: state => state.search.filters.transaction_type,
    //   property_type: state => state.search.filters.property_type,
    // }),
    // transactionType: {
    //   get() {
    //     return this.transaction_type;
    //   },
    //   set(newValue) {
    //     this.updateFilters({ transaction_type: newValue });
    //   }
    // },
    // propertyType: {
    //   get() {
    //     return this.property_type;
    //   },
    //   set(newValue) {
    //     this.updateFilters({ property_type: newValue });
    //   }
    // }
  },
}
</script>
